import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import IndiaMap from "../components/IndiaMap";
import RotatingRetailQuote from "../components/RotatingRetailQuote";
import { INDIAN_STATES } from "../data/indianStates";
import { RETAIL_NOUN } from "../data/retailQuotes";
import { ArrowUpRight, MapPin, ArrowRight, Sparkles, ShieldCheck, Users, Layers } from "lucide-react";

export default function RetailLeasingHome() {
  const [items, setItems] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const [state, setState] = useState("all");
  const [city, setCity] = useState("all");
  const [loading, setLoading] = useState(true);

  const refetch = (s = state, c = city) => {
    setLoading(true);
    const params = {};
    if (s && s !== "all") params.state = s;
    if (c && c !== "all") params.city = c;
    api.get("/retail-properties", { params }).then((r) => setItems(r.data.items || [])).finally(() => setLoading(false));
  };

  useEffect(() => { refetch(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    api.get("/retail-properties/cities", { params: state !== "all" ? { state } : {} })
      .then((r) => setAllCities(r.data.cities || []));
    setCity("all");
  }, [state]);
  useEffect(() => { refetch(state, city); /* eslint-disable-next-line */ }, [state, city]);

  const availableStates = useMemo(() => INDIAN_STATES, []);

  return (
    <div data-testid="retail-leasing-home">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-brand-50/70 via-white to-white" />
        <div className="absolute -top-32 -right-20 w-[600px] h-[600px] rounded-full bg-brand/10 blur-3xl -z-10" />

        <div className="container-zelo pt-16 md:pt-20 pb-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-neutral-200 text-xs font-semibold tracking-[0.2em] uppercase text-brand">
                <span className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse" /> Retail Leasing · Live Across India
              </div>
              <div className="mt-6">
                <RotatingRetailQuote />
              </div>
              <p className="mt-6 italic text-lg md:text-xl text-neutral-500 max-w-2xl text-balance" data-testid="retail-hero-noun">
                {RETAIL_NOUN}
              </p>
            </div>
            <div className="lg:col-span-5 grid grid-cols-3 gap-3">
              <Stat n="1,070+" l="Curated Spaces" />
              <Stat n="15+" l="States Covered" />
              <Stat n="Tier 1–3" l="Cities" small />
            </div>
          </div>

          {/* Filter bar — state + city only */}
          <div className="mt-10 bg-white border border-neutral-200 rounded-2xl shadow-soft p-2 flex flex-col md:flex-row gap-2 items-stretch" data-testid="retail-filter-bar">
            <div className="flex-1 px-4 py-2.5">
              <div className="label-tiny mb-1">State</div>
              <select value={state} onChange={(e) => setState(e.target.value)} className="w-full text-sm font-medium outline-none bg-transparent cursor-pointer" data-testid="retail-state-select">
                <option value="all">All States</option>
                {availableStates.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="hidden md:block w-px bg-neutral-200" />
            <div className="flex-1 px-4 py-2.5">
              <div className="label-tiny mb-1">City</div>
              <select value={city} onChange={(e) => setCity(e.target.value)} className="w-full text-sm font-medium outline-none bg-transparent cursor-pointer" data-testid="retail-city-select">
                <option value="all">{state === "all" ? "Pick a state first" : "All Cities"}</option>
                {allCities.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <button onClick={() => refetch(state, city)} className="rounded-full bg-brand text-white px-6 py-3 text-sm font-semibold hover:bg-brand-600 transition active:scale-95 flex items-center gap-2 justify-center" data-testid="retail-search-btn">
              Show Spaces <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* MAP */}
      <section className="container-zelo pb-12">
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="label-tiny text-brand">Map View</div>
            <h2 className="font-heading text-2xl md:text-3xl font-medium tracking-tight mt-1">{loading ? "Loading…" : `${items.length} space${items.length === 1 ? "" : "s"} on map`}</h2>
          </div>
          <div className="text-xs text-neutral-500 flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-brand inline-block" /> Click any pin to preview</div>
        </div>
        <IndiaMap items={items} onMarkerClick={(p) => { window.location.href = `/retail-leasing/${p.id}`; }} />
      </section>

      {/* RESULTS GRID — no building name on tile */}
      <section className="container-zelo pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="retail-results-grid">
          {items.map((p) => (
            <Link key={p.id} to={`/retail-leasing/${p.id}`} className="group block rounded-2xl border border-neutral-200 bg-white overflow-hidden hover:-translate-y-1.5 hover:shadow-soft transition-all duration-300" data-testid={`retail-card-${p.id}`}>
              <div className="relative aspect-[4/3] overflow-hidden bg-neutral-100">
                <img src={p.facade_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute top-3 left-3 bg-white/85 backdrop-blur px-2 py-0.5 rounded-md text-[9px] font-mono font-medium tracking-wider shadow-soft text-neutral-500">
                  {p.code || "ZRL—"}
                </div>
                <div className="absolute top-3 right-3 bg-white/95 backdrop-blur px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-soft text-neutral-700">
                  {p.possession}
                </div>
              </div>
              <div className="p-5">
                <div className="label-tiny text-brand">{p.property_type}</div>
                <div className="font-heading text-lg font-medium mt-1 line-clamp-1">{p.area}</div>
                <div className="flex items-center gap-1 text-sm text-neutral-500 mt-1"><MapPin className="w-3.5 h-3.5" />{p.city}, {p.state}</div>
                <div className="mt-4 grid grid-cols-2 gap-3 text-sm border-t border-neutral-100 pt-4">
                  <div><div className="label-tiny">Floor</div><div className="font-medium">{p.floor}</div></div>
                  <div><div className="label-tiny">Carpet</div><div className="font-medium">{p.carpet_total.toLocaleString("en-IN")} sqft</div></div>
                  <div><div className="label-tiny">Frontage</div><div className="font-medium">{p.frontage_ft} ft</div></div>
                  <div><div className="label-tiny">Height</div><div className="font-medium">{p.height_ft} ft</div></div>
                </div>
                <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand group-hover:gap-2.5 transition-all">View Details & Inquire <ArrowUpRight className="w-4 h-4" /></div>
              </div>
            </Link>
          ))}
        </div>
        {items.length === 0 && !loading && (
          <div className="rounded-2xl border border-dashed border-neutral-200 p-12 text-center">
            <div className="font-heading text-2xl font-medium">No spaces match those filters.</div>
            <div className="mt-2 text-neutral-500">Try a different state or city.</div>
          </div>
        )}
      </section>

      {/* WHY ZELO RETAIL */}
      <section className="bg-neutral-50/60 py-20 md:py-24">
        <div className="container-zelo">
          <div className="max-w-3xl mb-12">
            <div className="label-tiny text-brand">The Zelo Retail Edge</div>
            <h2 className="font-heading text-3xl md:text-5xl font-medium tracking-tight mt-3 text-balance">Built for brands that don't have time to scout.</h2>
            <p className="mt-4 text-neutral-500 text-lg leading-relaxed">
              Every space on Zelo Retail is hand-curated for brand fit, footfall, and frontage. We bring you only what passes our 12-point retail-readiness check — so your launch timeline doesn't depend on a builder's promises.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { Icon: Sparkles, t: "Brand-Fit Curated", d: "We match catchment, neighbour mix, and footfall before showing you the space." },
              { Icon: ShieldCheck, t: "Possession Guaranteed", d: "Every listing has a confirmed possession date. No 'will be ready soon'." },
              { Icon: Users, t: "Anchor Tenant Mix", d: "See nearby tenants up-front so you know exactly who you'll share footfall with." },
              { Icon: Layers, t: "All-Format Ready", d: "Showrooms, F&B shells, ground-floor anchors, drive-thrus, high-street stores — every format, every state." },
            ].map((p, i) => (
              <div key={i} className="rounded-2xl border border-neutral-200 bg-white p-6 hover:-translate-y-1 transition-all duration-300">
                <div className="w-10 h-10 rounded-lg bg-brand-50 text-brand flex items-center justify-center"><p.Icon className="w-5 h-5" /></div>
                <div className="font-heading text-lg font-medium mt-4">{p.t}</div>
                <div className="text-sm text-neutral-500 mt-2 leading-relaxed">{p.d}</div>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center flex flex-wrap gap-3 justify-center">
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-7 py-3.5 text-sm font-semibold hover:bg-brand-600 transition shadow-brand active:scale-95">
              Talk to a Retail Advisor <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/broker" className="inline-flex items-center gap-2 rounded-full border border-neutral-200 px-7 py-3.5 text-sm font-semibold hover:border-brand hover:text-brand transition">
              I'm a Broker · See Demands <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ n, l, small }) {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-4">
      <div className={`font-heading ${small ? "text-xl" : "text-3xl"} font-medium tracking-tight text-brand`}>{n}</div>
      <div className="label-tiny mt-1">{l}</div>
    </div>
  );
}
